import { z } from 'zod'

import {
  CustomerFormStatus,
  type ListInsiderMembersOrderingItem,
} from '@gto/api-client/admin/react-query'
import { listInsiderMembersQuerySearchMax } from '@gto/api-client/admin/zod'

import { trimmedTextParam } from '@/utils/search-params'

import {
  APP_USAGE_MISSING,
  APP_USAGE_VALUES,
  appUsageValues,
  type MemberFilters,
  NO_SUBSCRIPTION,
  POKER_FORMAT_VALUES,
  SOLVER_CONFIDENCE_VALUES,
  STAKES_VALUES,
  SUBSCRIPTION_TIER_VALUES,
  subscriptionTierValues,
  TRACK_VALUES,
  YEARS_BUCKETS,
} from './member-filters'

/**
 * The `/insider/members` URL contract, kept beside the page so the route
 * declaration, the page and their tests read one definition.
 *
 * `page` is 1-based — what the address bar shows — and the page converts it to
 * the admin API's `offset`. The admin surface pages on `limit`/`offset` and
 * sorts on `ordering`; `page`/`page_size` belong to a DIFFERENT backend that
 * `affiliates-web` talks to, and this endpoint would ignore them silently while
 * the table claimed a page it never asked for.
 */

/** No `status` reaches the API, so every lifecycle comes back together. */
export const ALL_MEMBER_STATUSES = 'all'

/**
 * Derived from the generated enum rather than re-listed, so a lifecycle status
 * added on the backend becomes reachable from the filter the moment the client
 * is regenerated. Spelling the three out again type-checks forever and would
 * leave the new cohort selectable by hand-edited URL only.
 */
export const MEMBER_STATUS_FILTERS = [
  ...Object.values(CustomerFormStatus),
  ALL_MEMBER_STATUSES,
] as const

export type MemberStatusFilter = (typeof MEMBER_STATUS_FILTERS)[number]

/**
 * Ending a membership writes an inactive row rather than deleting one, so the
 * unfiltered list is mostly former members within a year. Active is the default
 * the list opens on, and the filter is how former members stay reachable.
 */
export const DEFAULT_MEMBER_STATUS = CustomerFormStatus.active

/**
 * `satisfies` ties the list to the generated union: if the backend drops a sort
 * key, this stops compiling instead of shipping a param the API answers with a
 * 422. The reverse — a key added on the backend — is deliberately not covered,
 * because the FE only offers sorts it renders a column for.
 */
const MEMBER_ORDERING_VALUES = [
  'submitted_at',
  '-submitted_at',
  'updated_at',
  '-updated_at',
  'email',
  '-email',
  'status',
  '-status',
  'last_login',
  '-last_login',
  'date_joined',
  '-date_joined',
] as const satisfies readonly ListInsiderMembersOrderingItem[]

export type MemberOrdering = (typeof MEMBER_ORDERING_VALUES)[number]

/** The backend's own default: newest first, which is what `First joined` shows. */
export const DEFAULT_MEMBER_ORDERING: MemberOrdering = '-submitted_at'

/**
 * The backend's own cap, imported rather than restated: a prose comment naming
 * a number is exactly the drift that survives an orval regeneration silently —
 * a relaxed cap would leave the field swallowing keystrokes it could now send,
 * and a tightened one would let the operator type into a 422.
 */
export const MEMBER_SEARCH_MAX_LENGTH = listInsiderMembersQuerySearchMax

// An out-of-range status is DROPPED rather than forwarded: the endpoint answers
// an unknown one with a 422, so passing it through would turn a mistyped link
// into an error page, where dropping it degrades to the default view the reader
// can then narrow themselves.
const statusSchema = z.enum(MEMBER_STATUS_FILTERS).optional().catch(undefined)

const orderingSchema = z.enum(MEMBER_ORDERING_VALUES).optional().catch(undefined)

/**
 * A multi-valued facet in the URL.
 *
 * Unknown members are DROPPED rather than rejecting the whole link: a facet
 * value retired on the backend would otherwise turn every bookmark carrying it
 * into an error page, where dropping it degrades to a slightly wider cohort the
 * reader can narrow again. `.catch` covers the shape being wrong outright (a
 * bare string where an array belongs); the inner `.catch(undefined)` plus
 * `filter` covers one bad member among good ones, which the outer one cannot
 * see.
 *
 * An emptied facet becomes `undefined` so it leaves the address bar entirely —
 * `?stakes=[]` and no `stakes` key describe the same cohort, and a URL with two
 * spellings is a URL that says more than it means.
 */
const facetSchema = <TValue extends string>(values: readonly [TValue, ...TValue[]]) =>
  z
    .array(z.enum(values).optional().catch(undefined))
    .transform((parsed) => parsed.filter((value): value is TValue => value !== undefined))
    .transform((parsed) => (parsed.length === 0 ? undefined : parsed))
    .optional()
    .catch(undefined)

const yearsSchema = z.enum(YEARS_BUCKETS).optional().catch(undefined)

// Only `true` is carried. The sentinels are OR-ed INTO their facet, so `false`
// and absent mean the same thing, and writing `=false` into the URL would imply
// an "exclude the never-asked" mode that does not exist.
const sentinelSchema = z
  .boolean()
  .optional()
  .catch(undefined)
  .transform((value) => (value === true ? true : undefined))

export const insiderMembersSearchSchema = z.object({
  // `.catch` for the same reason the three below carry it: a hand-edited or
  // stale `?page=` is a mistyped link, and throwing out of `validateSearch`
  // turns it into an error page where dropping it degrades to page 1.
  page: z.coerce.number().int().positive().optional().catch(undefined),
  status: statusSchema,
  search: trimmedTextParam(MEMBER_SEARCH_MAX_LENGTH),
  ordering: orderingSchema,
  // Named exactly as the endpoint's own parameters, so a cohort URL and the
  // request it produces can be read against each other without a translation
  // table. The one exception is `years`, a bucket token the page expands into
  // the numeric `years_min` / `years_max` the endpoint takes — the bucket is
  // what a reader picked and what a shared link should still mean after C-1911
  // moves a boundary.
  poker_formats: facetSchema(POKER_FORMAT_VALUES),
  stakes: facetSchema(STAKES_VALUES),
  solver_confidence: facetSchema(SOLVER_CONFIDENCE_VALUES),
  years: yearsSchema,
  tracks: facetSchema(TRACK_VALUES),
  app_usage: facetSchema(APP_USAGE_VALUES),
  app_usage_missing: sentinelSchema,
  subscription_tier: facetSchema(SUBSCRIPTION_TIER_VALUES),
  no_subscription: sentinelSchema,
})

export type InsiderMembersSearch = z.infer<typeof insiderMembersSearchSchema>

/**
 * URL state → the cohort model. The two sentinels rejoin their facet here, and
 * only here: everything downstream — the panel, the reveal, the empty state,
 * the export — reads a `MemberFilters` in which "never asked" is just another
 * ticked box, which is what it is to the person who ticked it.
 */
const withSentinel = <TValue extends string, TSentinel extends string>(
  values: TValue[] | undefined,
  sentinel: TSentinel,
  include: boolean,
): (TValue | TSentinel)[] => (include ? [...(values ?? []), sentinel] : [...(values ?? [])])

export const filtersFromSearch = (search: InsiderMembersSearch): MemberFilters => ({
  poker_formats: search.poker_formats ?? [],
  stakes: search.stakes ?? [],
  solver_confidence: search.solver_confidence ?? [],
  years: search.years,
  tracks: search.tracks ?? [],
  app_usage: withSentinel(search.app_usage, APP_USAGE_MISSING, search.app_usage_missing === true),
  subscription: withSentinel(
    search.subscription_tier,
    NO_SUBSCRIPTION,
    search.no_subscription === true,
  ),
})

/**
 * Every key mandatory, every value still free to be `undefined`.
 *
 * `Required` is the wrong tool: its `-?` strips `undefined` out of the VALUE as
 * well as the `?` off the key, which would forbid the one thing a cleared facet
 * has to write — and re-uniting `undefined` afterwards does not help, because
 * the modifier is applied to the result. Mapping over `keyof T & string` is not
 * homomorphic, so no optional modifier is copied across and the values survive
 * intact.
 */
type EveryKeyPresent<T> = { [K in keyof T & string]: T[K] }

/**
 * The cohort model → URL state, as a patch over the existing search.
 *
 * Every key is written on every call, `undefined` included, because a facet the
 * operator just cleared has to be REMOVED from the address bar — spreading only
 * the non-empty ones would leave the old value behind and the link would keep
 * filtering by something no box is ticked for.
 *
 * `EveryKeyPresent` is what makes that a rule rather than a habit. Every key of
 * the inferred search type is optional — zod marks a key optional whenever
 * `undefined` is in its output, which is true of all of them — so without it a
 * facet added to the schema and forgotten here compiles fine and the URL simply
 * never carries it.
 */
export const searchFromFilters = (
  filters: MemberFilters,
): EveryKeyPresent<Omit<InsiderMembersSearch, 'page' | 'status' | 'search' | 'ordering'>> => {
  const orUndefined = <T>(values: T[]): T[] | undefined =>
    values.length === 0 ? undefined : values

  return {
    poker_formats: orUndefined(filters.poker_formats),
    stakes: orUndefined(filters.stakes),
    solver_confidence: orUndefined(filters.solver_confidence),
    years: filters.years,
    tracks: orUndefined(filters.tracks),
    app_usage: orUndefined(appUsageValues(filters.app_usage)),
    app_usage_missing: filters.app_usage.includes(APP_USAGE_MISSING) ? true : undefined,
    subscription_tier: orUndefined(subscriptionTierValues(filters.subscription)),
    no_subscription: filters.subscription.includes(NO_SUBSCRIPTION) ? true : undefined,
  }
}

/**
 * The sortable columns, each carrying both directions of its backend sort key.
 * A column absent here is not sortable at all — every parameter the signup blob
 * holds is a JSONField lookup where `->>` yields TEXT, and `features` is
 * resolved in Python per page with no SQL expression, so offering those sorts
 * would order `"10"` before `"9"` and look exactly like a correct result.
 *
 * One list rather than a record plus a key array: the two would be a pair that
 * can disagree, and `sortingFromOrdering` has to search the values anyway.
 */
const SORTABLE_COLUMNS = [
  { id: 'email', asc: 'email', desc: '-email' },
  { id: 'status', asc: 'status', desc: '-status' },
  { id: 'joined', asc: 'submitted_at', desc: '-submitted_at' },
  { id: 'updated', asc: 'updated_at', desc: '-updated_at' },
  { id: 'lastSeen', asc: 'last_login', desc: '-last_login' },
  { id: 'accountCreated', asc: 'date_joined', desc: '-date_joined' },
] as const satisfies readonly { id: string; asc: MemberOrdering; desc: MemberOrdering }[]

export type SortableColumnId = (typeof SORTABLE_COLUMNS)[number]['id']

/**
 * The sortable column ids, as values the table's `ColumnDef`s take their `id`
 * from. Nothing else ties the two lists together: a column id renamed on one
 * side alone leaves `orderingFromSorting` falling through to the default, which
 * renders as a header that quietly refuses to sort. Reading the id from here
 * makes that rename a compile error instead.
 */
export const SORTABLE_COLUMN_ID = {
  email: 'email',
  status: 'status',
  joined: 'joined',
  updated: 'updated',
  lastSeen: 'lastSeen',
  accountCreated: 'accountCreated',
} as const satisfies Record<SortableColumnId, SortableColumnId>

export type MemberSorting = { id: SortableColumnId; desc: boolean }

/** `[{ id, desc }]` (what `DataTable` speaks) → the API's `ordering` value. */
export const orderingFromSorting = (
  sorting: readonly { id: string; desc: boolean }[],
): MemberOrdering => {
  const [first] = sorting
  if (first === undefined) {
    return DEFAULT_MEMBER_ORDERING
  }

  const column = SORTABLE_COLUMNS.find((candidate) => candidate.id === first.id)
  if (column === undefined) {
    return DEFAULT_MEMBER_ORDERING
  }

  return first.desc ? column.desc : column.asc
}

/** The API's `ordering` value → `[{ id, desc }]`, so the header shows the arrow. */
export const sortingFromOrdering = (ordering: MemberOrdering): MemberSorting[] => {
  const column = SORTABLE_COLUMNS.find(
    (candidate) => candidate.asc === ordering || candidate.desc === ordering,
  )
  // Unreachable while every `MemberOrdering` has a column — which is the point:
  // it is what an ordering key added without one degrades to, rather than
  // throwing at a reader who followed a link.
  if (column === undefined) {
    return []
  }

  return [{ id: column.id, desc: column.desc === ordering }]
}
